import { useState, useEffect } from 'preact/hooks';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-preact';
import type { Risk } from '../../types/api';
import { extractRiskCityName } from '../../functions/geospatial';
import { formatPublicationDayLabel, publicationDayMs } from './geospatialPublicationRange';

export interface RiskDetailCardProps {
  /** All risks/opportunities for the selected area; empty = show empty state. */
  risks: Risk[];
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const ms = publicationDayMs(iso);
  return ms != null ? formatPublicationDayLabel(ms) : iso;
}

/** First character uppercase, remainder unchanged (preserves acronyms). */
function capitalizeFirstChar(value: string | undefined | null): string {
  const s = value?.trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** First letter of each word uppercase, rest lowercase (e.g. analyst → Analyst, CAIRO → Cairo). */
function titleCaseWords(value: string | undefined | null): string {
  if (!value?.trim()) return '';
  return value
    .trim()
    .split(/\s+/)
    .map((word) => {
      if (!word) return '';
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .filter(Boolean)
    .join(' ');
}

/** Country and city only (title-cased), e.g. "Egypt, Cairo" — rendered in the card footer. */
function buildCountryCityLine(risk: Risk): string {
  const country = titleCaseWords(risk.country);
  const cityRaw = extractRiskCityName(risk.location)?.trim() || (risk.location || '').trim();
  const city = titleCaseWords(cityRaw);
  return [country, city].filter(Boolean).join(', ');
}

function fullDescription(risk: Risk): string | null {
  const text = risk.description?.trim();
  return text || null;
}

/** Normalize source URLs so links open reliably in a new tab. */
function resolveSourceUrl(raw: string | undefined | null): string | null {
  const value = raw?.trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('//')) return `https:${value}`;
  return `https://${value}`;
}

/** Shared body for one risk/opportunity card (carousel and insight list). */
export function RiskContent({ risk }: { risk: Risk }) {
  const isOpportunity = risk.is_positive === true;
  const typeLabel = isOpportunity ? 'Opportunity' : 'Risk';
  const description = fullDescription(risk);
  const sourceLabel =
    [capitalizeFirstChar(risk.originator), capitalizeFirstChar(risk.source_type)].filter(Boolean).join(' · ') || '—';
  const dateStr = formatDate(risk.publication_date);
  const countryCityLine = buildCountryCityLine(risk);
  const sourceUrl = resolveSourceUrl(risk.link_to_source);
  const tone = isOpportunity ? 'opp' : 'risk';

  return (
    <>
      <div className="risk-detail-card__main">
        <div className="risk-detail-card__top">
          <div className={`risk-detail-card__sentiment-badge risk-detail-card__sentiment-badge--${tone}`}>
            {typeLabel}
          </div>
          {sourceUrl && (
            <a
              className={`risk-detail-card__source-link risk-detail-card__source-link--${tone}`}
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={13} aria-hidden="true" />
              <span>Source</span>
            </a>
          )}
        </div>

        <div className="risk-detail-card__body">
          {description ? (
            <div className="risk-detail-card__description">{description}</div>
          ) : (
            <p className="risk-detail-card__headline">{countryCityLine || '—'}</p>
          )}
        </div>

        {(risk.impact_target || risk.risk_category) && (
          <div className="risk-detail-card__tags">
            {risk.impact_target && (
              <span className={`risk-detail-card__impact risk-detail-card__impact--${tone}`}>
                {risk.impact_target}
              </span>
            )}
            {risk.risk_category && (
              <span className={`risk-detail-card__category risk-detail-card__category--${tone}`}>
                {risk.risk_category}
              </span>
            )}
          </div>
        )}

        <div className="risk-detail-card__source">
          <div className="risk-detail-card__source-left">
            <span className="risk-detail-card__source-publisher">{sourceLabel}</span>
            {countryCityLine && (
              <>
                <span className="risk-detail-card__source-join" aria-hidden="true">
                  ·
                </span>
                <span className="risk-detail-card__source-location">{countryCityLine}</span>
              </>
            )}
          </div>
          <span className="risk-detail-card__source-date">{dateStr}</span>
        </div>
      </div>
    </>
  );
}

export function RiskDetailCard({ risks }: RiskDetailCardProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(0);
  }, [risks]);

  if (!risks.length) {
    return (
      <div className="risk-detail-card-carousel" aria-label="No area selected">
        <div className="risk-detail-card risk-detail-card--filled risk-detail-card--no-area">
          <div className="risk-detail-card__main risk-detail-card__main--no-area">
            <p className="risk-detail-card__no-area-message">
              Select an area on the map to show its details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const risk = risks[currentIndex];
  const isOpportunity = risk.is_positive === true;
  const typeLabel = isOpportunity ? 'Opportunity' : 'Risk';
  const total = risks.length;
  const canPrev = total > 1;
  const canNext = total > 1;

  const goPrev = () => setCurrentIndex((i) => (i <= 0 ? total - 1 : i - 1));
  const goNext = () => setCurrentIndex((i) => (i >= total - 1 ? 0 : i + 1));

  return (
    <div className="risk-detail-card-carousel" aria-label="Navigate between items">
      <article
        className={`risk-detail-card risk-detail-card--filled risk-detail-card--${isOpportunity ? 'opp' : 'risk'}`}
        aria-label={`Details: ${typeLabel} ${currentIndex + 1} of ${total}`}
      >
        <RiskContent risk={risk} />

        <div className={`risk-detail-card__arrows risk-detail-card__arrows--${isOpportunity ? 'opp' : 'risk'}`}>
          <button
            type="button"
            className="risk-detail-card__slider-btn risk-detail-card__slider-btn--prev"
            onClick={goPrev}
            disabled={!canPrev}
            aria-label="Previous risk or opportunity"
          >
            <ChevronLeft size={16} aria-hidden />
          </button>
          <div className="risk-detail-card__dots" role="tablist" aria-label="Slide index">
            {Array.from({ length: total }, (_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === currentIndex}
                aria-label={`Go to item ${i + 1}`}
                className={`risk-detail-card__dot ${i === currentIndex ? 'risk-detail-card__dot--active' : ''}`}
                onClick={() => setCurrentIndex(i)}
              />
            ))}
          </div>
          <button
            type="button"
            className="risk-detail-card__slider-btn risk-detail-card__slider-btn--next"
            onClick={goNext}
            disabled={!canNext}
            aria-label="Next risk or opportunity"
          >
            <ChevronRight size={16} aria-hidden />
          </button>
        </div>
      </article>
    </div>
  );
}
